/**
 * Your account — where a person sets their own password (James, on alpha.41: the code signs you
 * in, and then there has to be somewhere to set one; it is not on the door, by his own ruling).
 *
 * A page of its own inside the shell, reached from the bar's own menu. It says who is signed in,
 * how this sign-in was proved and what second factor the account holds, and it carries the one
 * thing a person can do about their own way in: set a new password. A session that came in by a
 * code, a link or a passkey has already proved the address or the device, so it may set one
 * outright — that is the way back for somebody who has forgotten theirs; a password session must
 * give the current one first. The account screen is not on the canvas yet (defect #155).
 */
import { useState, type ReactNode } from 'react';
import { Button, Field, Form, Input, PageBody, PageHeader } from '@cider/ui';
import { api, sentence } from '../../shell/api.js';
import type { Me } from '../../shell/session.js';
import { YourSignIns } from './YourSignIns.js';

const LANE_SAYS: Record<string, string> = {
  password: 'your email and password',
  passkey: 'a passkey on this device',
  magic_link: 'a one-time code or link we emailed you',
  microsoft: 'your festival Microsoft 365 account',
  device: 'this device’s token and PIN',
};

export function Account({
  me,
  onSignedIn,
}: {
  me: Me;
  onSignedIn: () => Promise<void>;
}): ReactNode {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [again, setAgain] = useState('');
  const [busy, setBusy] = useState(false);
  const [said, setSaid] = useState<string | null>(null);
  const [refusal, setRefusal] = useState<string | null>(null);

  const lane = me.session?.lane ?? 'password';
  // A session that proved the address or the device sets a password without the old one.
  const proved = lane === 'magic_link' || lane === 'passkey';
  const factors = me.factorsSet ?? { passkey: false, totp: false };
  const holds = [
    factors.passkey ? 'a passkey' : null,
    factors.totp ? 'an authenticator code' : null,
  ]
    .filter((x): x is string => x !== null)
    .join(' and ');

  const save = (): void => {
    setRefusal(null);
    setSaid(null);
    if (next !== again) {
      setRefusal('The two new passwords are not the same.');
      return;
    }
    setBusy(true);
    void api<{ says: string }>('POST', '/auth/password', {
      ...(proved ? {} : { current }),
      password: next,
    })
      .then(async (r) => {
        setSaid(r.says);
        setCurrent('');
        setNext('');
        setAgain('');
        await onSignedIn();
      })
      .catch((err: unknown) => {
        setRefusal(sentence(err));
      })
      .finally(() => {
        setBusy(false);
      });
  };

  return (
    <>
      <PageHeader
        eyebrow="YOUR ACCOUNT"
        title={me.name ?? me.email ?? 'Your account'}
        lede="Who is signed in on this device, how it was proved, and the one thing you can change about it yourself."
      />
      <PageBody>
        <dl className="ui-facts">
          <dt>Email</dt>
          <dd>{me.email ?? 'NO DATA'}</dd>
          <dt>Signed in with</dt>
          <dd>{LANE_SAYS[lane] ?? lane}</dd>
          <dt>Second factor</dt>
          <dd>
            {holds === ''
              ? 'None set. A director’s account asks for one at the next sign-in.'
              : `This account holds ${holds}. Another director clears it — nobody clears their own.`}
          </dd>
        </dl>

        <Form onSubmit={save} refusal={refusal} busy={busy}>
          <h2>Set a new password</h2>
          <p>
            {proved
              ? 'You came in with a code, a link or a passkey, which proves this is your address — so you can set a new password without the old one.'
              : 'Give the password you are using now, then the new one twice.'}
          </p>
          {proved ? null : (
            <Field label="Your password now" id="current">
              <Input
                id="current"
                type="password"
                autoComplete="current-password"
                value={current}
                onChange={(e) => {
                  setCurrent(e.target.value);
                }}
              />
            </Field>
          )}
          <Field label="New password" id="new" hint="Twelve or more characters. A sentence works.">
            <Input
              id="new"
              type="password"
              autoComplete="new-password"
              value={next}
              onChange={(e) => {
                setNext(e.target.value);
              }}
            />
          </Field>
          <Field label="The new one again" id="again">
            <Input
              id="again"
              type="password"
              autoComplete="new-password"
              value={again}
              onChange={(e) => {
                setAgain(e.target.value);
              }}
            />
          </Field>
          <p>
            <Button kind="primary" type="submit" busy={busy}>
              Set my password
            </Button>
          </p>
          {said ? <p role="status">{said}</p> : null}
        </Form>
        <YourSignIns me={me} />
      </PageBody>
    </>
  );
}
